import React, { useState } from "react";
import {
  Container,
  Box,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Menu,
  MenuItem,
  useTheme,
  Typography,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Close,
  MoreVert,
  RemoveRedEyeOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { NURSING_APPROVALS } from "../_mock/mock";
import { Link, useNavigate } from "react-router-dom";
import {
  createConsultationBedTransfersByTrackingIdApi,
  geBedTransferApprovalApi,
} from "../services/consultation.service";
import { useGetApi } from "../hooks/useGetApi";
import dayjs from "dayjs";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import ApprovalViewDetails from "../components/nursing/modals/approval-view-details";
import ConfirmationDialog from "../components/confirmation-dialog/confirmation-dialog";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const headLabel = [
  {
    id: 1,
    label: "UHID",
    value: "uhid",
  },
  {
    id: 2,
    label: "Date",
    value: "date",
  },
  {
    id: 3,
    label: "Patient Name",
    value: "patientName",
  },
  {
    id: 4,
    label: "Ward",
    value: "ward",
  },
  {
    id: 5,
    label: "Bed",
    value: "bed",
  },
  {
    id: 6,
    label: "New Ward",
    value: "newWard",
  },
  {
    id: 7,
    label: "New Bed",
    value: "newBed",
  },
  {
    id: 8,
    label: "Request Type",
    value: "requestType",
  },
  {
    id: 9,
    label: "",
    value: "",
    align: "right",
  },
  {
    id: 10,
    label: "",
    value: "",
    align: "right",
  },
];

const popoverOptions = ["Accept", "View Details", "Decline"];

const buttons = [
  { label: "All", value: "all", width: "90px" },
  { label: "Pending", value: "pending", width: "90px" },
  { label: "Approved", value: "approved", width: "90px" },
];

const NursingApprovals = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [patientsList, setPatientsList] = useState(NURSING_APPROVALS || []);
  const [patientsCount, setPatientsCount] = useState(
    NURSING_APPROVALS?.length ?? 0
  );
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeButton, setActiveButton] = useState("All");
  const [dateFilter, setDateFilter] = useState("all");
  const [status, setStatus] = useState("all");
  const [approvalViewModalOpen, setApprovalViewModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [loading, setLoading] = useState(false);
  const navigation = useNavigate();

  const {
    dataList: approvalDataObj,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: geBedTransferApprovalApi,
    params: {
      limit: rowsPerPage,
      page: page + 1,
      status: status,
      dateFilter: dateFilter,
    },
    dependencies: [page, rowsPerPage, status, dateFilter],
    dataKey: 'data',
  });

  // Extract requests and total from the returned object
  const approvalRequests = approvalDataObj?.requests || [];
  const approvalTotal = approvalDataObj?.total || 0;

  const handleModalOpen = (index) => {
    handleClose();
    switch (index) {
      case 0:
        setModalType("accept");
        setConfirmationModalOpen(true);
        break;
      case 1:
        setApprovalViewModalOpen(true);
        break;
      case 2:
        setModalType("decline");
        setConfirmationModalOpen(true);
        break;
      default:
        break;
    }
  };
  const handleConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
    setModalType("");
  };
  const handleAccept = async () => {
    let response;
    if (selectedRow?._id) {
      setLoading(true);
      response = await createConsultationBedTransfersByTrackingIdApi(
        selectedRow?._id,
        {
          action: "approve",
        }
      );
      setLoading(false);

      if (response.code === 200 || response.code === 201) {
        toast.success(response.message);
        refetch();
      } else {
        toast.error(response.message || "Some error occurred.");
      }
    }
  };

  const handleDecline = async () => {
    let response;
    if (selectedRow?._id) {
      setLoading(true);
      response = await createConsultationBedTransfersByTrackingIdApi(
        selectedRow?._id,
        {
          action: "decline",
        }
      );
      setLoading(false);
      if (response.code === 200 || response.code === 201) {
        toast.success(response.message);
        refetch();
      } else {
        toast.error(response.message || "Some error occurred.");
      }
    }
  };

  const handleConfirmation = async () => {
    if (modalType === "accept") {
      await handleAccept();
    } else if (modalType === "decline") {
      await handleDecline();
    }
    handleConfirmationModalClose();
  };

  const handleRightClick = (event, row) => {
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
        <Button
          // component={Link}
          to="/patient/packages/details" // replace with the path you want to navigate to
          startIcon={<ArrowBackIcon />}
          onClick={() => navigation(-1)}
          sx={{
            color: '#5D6A82', 
            textTransform: 'none', 
            fontSize: '16px',
            fontWeight: 'bold',
            padding: 0,
            transition: 'none !important',
          }}
        >
          Back
        </Button>
          <Typography
            variant="h6"
            sx={{ mt: "20px", mb: "10px", color: "#5D6A82" }}
          >
            Approval Requests
          </Typography>
          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                backgroundColor: "white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  my: "20px",
                }}
              >
                <FormControl size="small" sx={{ minWidth: "150px" }}>
                  <InputLabel id="date-select-label">Date</InputLabel>
                  <Select
                    labelId="date-select-label"
                    label="Date"
                    value={dateFilter || ""}
                    onChange={(e) => setDateFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {buttons?.map((button) => (
                <Button
                  key={button?.label}
                  variant="outlined"
                  onClick={() => {
                    setActiveButton(button?.label);
                    setStatus(button?.value);
                  }}
                  sx={{
                    border: `2px solid ${
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "#c0c0c0"
                    }`,
                    color:
                      activeButton === button?.label
                        ? theme.palette.primary.dark
                        : "text.secondary",
                    bgcolor:
                      activeButton === button?.label ? "#AED5F3" : "#F0F7FC",
                    fontWeight: activeButton === button?.label ? 600 : 400,
                    width: button?.width || "100px",
                    textTransform: "none",
                  }}
                >
                  {button?.label || ""}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Table */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table
                  aria-label="nursing all medication tracking"
                  sx={{ minWidth: 800 }}
                >
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {headLabel?.map((option) => (
                        <TableCell
                          key={option?.id}
                          sx={{ textAlign: option?.align || "left" }}
                        >
                          {option?.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    
                    {approvalRequests?.map((row) => (
                      <TableRow
                        key={row?._id}
                        onContextMenu={(event) => handleRightClick(event, row)}
                      >
                        <TableCell>{row?.patient?.UHID || "-"}</TableCell>
                        <TableCell>
                          {row?.requestedDate
                            ? dayjs(row?.requestedDate).format("YYYY-MM-DD")
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {`${row?.patient?.firstName || ""} ${
                            row?.patient?.lastName || ""
                          }` || "-"}
                        </TableCell>
                        <TableCell>
                          {row?.currentWard?.wardName || "-"}
                        </TableCell>
                        <TableCell>
                          {row?.currentBed?.bedNumber || "-"}
                        </TableCell>
                        <TableCell>
                          {row?.requestedWard?.wardName || "-"}
                        </TableCell>
                        <TableCell>
                          {row?.requestedBed?.bedNumber || "-"}
                        </TableCell>
                        <TableCell>{row?.status || "-"}</TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <MoreVert
                            sx={{ cursor: "pointer" }}
                            onClick={(event) => handleClick(event, row)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}

              <TablePagination
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                component="div"
                count={approvalTotal || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
          {/* Pop-up Menu */}

          <Menu
            id={Boolean(anchorEl) ? "row-popover" : undefined}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
            anchorPosition={
              anchorPosition
                ? { top: anchorPosition.top, left: anchorPosition.left }
                : undefined
            }
            anchorOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "8px",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  maxHeight: "300px",
                },
              },
            }}
          >
            {popoverOptions?.map((option, index) => (
              <MenuItem onClick={() => handleModalOpen(index)}>
                {option || ""}
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <ApprovalViewDetails
          open={approvalViewModalOpen}
          onClose={() => setApprovalViewModalOpen(false)}
          details={selectedRow}
        />
        <ConfirmationDialog
          open={confirmationModalOpen}
          onCancel={handleConfirmationModalClose}
          onConfirm={handleConfirmation}
          isLoading={loading}
          title={
            modalType === "accept"
              ? "Are you sure you want to Accept Transfer Request"
              : "Are you sure you want to Decline Transfer Request"
          }
        />
      </Box>
    </Container>
  );
};

export default NursingApprovals;
